import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  Type,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { CommissioningStore, ZIGBEE_COORDINATOR_STATUS } from '@store';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ModalAction } from '../commissioning.model';
import { CommissioningService } from '../commissioning.service';
import {
  CommisioningComponent,
  CommissioningStrategy,
  initialStrategy,
} from '../commissioning.strategy';
import { CommissioningPairingResultModalComponent } from '../commissioning-pairing-result-modal/commissioning-pairing-result-modal.component';
import { CommissioningFinalResultModalComponent } from '../commissioning-final-result-modal/commissioning-final-result-modal.component';
import { CommissioningAssignmentLoaderModalComponent } from '../commissioning-assignment-loader-modal/commissioning-assignment-loader-modal.component';
import { CommissioningCodeModalComponent } from '../commissioning-code-modal/commissioning-code-modal.component';

@Component({
  template: `<div #componentModalContainer></div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissioningProcessModalComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('componentModalContainer', { read: ViewContainerRef })
  componentModalContainer: ViewContainerRef;

  // Enables support for multiple components of the same type within the strategy
  startSearchFromIndex = 0;

  private activeStrategy: CommissioningStrategy;
  private activeComponent: Type<CommisioningComponent>;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<CommissioningProcessModalComponent>,
    public commissioningStore: CommissioningStore,
    private commissioningService: CommissioningService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.commissioningStore.initCommissioning(false);
    this.commissioningStore.listFeatures();

    this.commissioningService.modalAction$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((action: ModalAction) => {
        switch (action) {
          case ModalAction.NavigateToStart: {
            this.selectDefaultStrategy();
            break;
          }
          case ModalAction.NavigateToDeviceAssign: {
            this.navigatetoDeviceAssign();
            break;
          }
          case ModalAction.NavigateToReadyToConnect: {
            this.navigateToReadyToConnect();
            break;
          }
          case ModalAction.NavigateToFinalStep: {
            this.navigateToFinalStep();
            break;
          }
          case ModalAction.Close: {
            this.close();
            this.commissioningService.currentRemoteDeviceCode$.next({
              id: null,
              ic: null,
            });
            this.commissioningService.pairingStatusName$.next(ZIGBEE_COORDINATOR_STATUS.INITIATING);
            break;
          }
          case ModalAction.NextStep: {
            this.nextStep();
            break;
          }
          case ModalAction.PreviousStep: {
            this.previousStep();
            break;
          }
          default: {
            break;
          }
        }
      });
  }

  ngAfterViewInit(): void {
    this.selectDefaultStrategy();

    this.commissioningService.activeStrategy$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((strategy) => {
        if (strategy !== this.activeStrategy) {
          this.selectStrategy(strategy);
        }
      });
  }

  selectDefaultStrategy(): void {
    this.startSearchFromIndex = 0;
    this.activeStrategy = initialStrategy();
    this.activeComponent = this.activeStrategy.steps[0];

    this.loadComponent(this.activeComponent);
  }

  navigatetoDeviceAssign(): void {
    const firstDeviceAssignComponent = this.activeStrategy.steps.find((entry) => {
      return entry === CommissioningPairingResultModalComponent;
    });
    const firstDeviceAssignComponentIndex =
      this.activeStrategy.steps.indexOf(firstDeviceAssignComponent) + 1;
    this.activeComponent = this.activeStrategy.steps[firstDeviceAssignComponentIndex];
    this.startSearchFromIndex = firstDeviceAssignComponentIndex;

    this.loadComponent(this.activeComponent);
  }

  navigateToReadyToConnect(): void {
    const commissioningModalTypeComponent = this.activeStrategy.steps.find((entry) => {
      return entry === CommissioningCodeModalComponent;
    });
    const commissioningModalTypeComponentIndex = this.activeStrategy.steps.indexOf(
      commissioningModalTypeComponent
    );
    this.activeComponent = this.activeStrategy.steps[commissioningModalTypeComponentIndex];
    this.startSearchFromIndex = commissioningModalTypeComponentIndex;

    this.loadComponent(this.activeComponent);
  }

  navigateToFinalStep(): void {
    const firstDeviceAssignComponent = this.activeStrategy.steps.find((entry) => {
      return entry === CommissioningFinalResultModalComponent;
    });
    const finalStepComponentIndex = this.activeStrategy.steps.indexOf(firstDeviceAssignComponent);
    this.activeComponent = this.activeStrategy.steps[finalStepComponentIndex];
    this.startSearchFromIndex = finalStepComponentIndex;

    this.loadComponent(this.activeComponent);
  }

  selectStrategy(strategy: CommissioningStrategy): void {
    this.activeStrategy = strategy;
    this.activeComponent = strategy.steps[0];
    this.startSearchFromIndex = 0;
  }

  loadComponent(component: Type<CommisioningComponent>): void {
    this.componentModalContainer.clear();
    this.componentModalContainer.createComponent<CommisioningComponent>(component);
    this.activeComponent = component;

    setTimeout(() => {
      this.cdr.markForCheck();
    }, 0);
  }

  previousStep(): void {
    // startSearchFromIndex
    // special case for handling navigation when there are multiple entries of the same component
    this.startSearchFromIndex = this.startSearchFromIndex > 0 ? this.startSearchFromIndex - 1 : 0;

    const currentIndex = this.activeStrategy.steps.indexOf(
      this.activeComponent,
      this.startSearchFromIndex
    );

    let previousIndex = currentIndex - 1;
    let previousComponent = this.activeStrategy.steps[previousIndex];

    // Skip loader when navigating back
    if (previousComponent === CommissioningAssignmentLoaderModalComponent) {
      previousComponent = this.activeStrategy.steps[previousIndex - 1];
    }

    if (previousComponent) {
      this.loadComponent(previousComponent);
    }
  }

  nextStep(): void {
    const currentIndex = this.activeStrategy.steps.indexOf(
      this.activeComponent,
      this.startSearchFromIndex
    );
    // startSearchFromIndex
    // special case for handling navigation when there are multiple entries of the same component
    this.startSearchFromIndex = currentIndex;

    const nextIndex = currentIndex + 1;
    const nextComponent = this.activeStrategy.steps[nextIndex];

    if (nextComponent) {
      this.loadComponent(nextComponent);
    }
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }
}
